import { useState } from "react";
import { Paperclip, Loader2, ExternalLink, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReceiptDropZone } from "@/components/controller/ReceiptDropZone";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  useExpenseAttachments,
  useUploadExpenseAttachments,
  useDeleteExpenseAttachment,
  type ExpenseAttachment,
} from "@/hooks/useExpenseAttachments";

const fmtSize = (bytes: number | null) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface Props {
  /** When provided, the component is in EDIT mode and persists files immediately. */
  expenseId?: string | null;
  /** Legacy single receipt path (back-compat). Edit mode only. */
  legacyReceiptPath?: string | null;
  /** Notify parent the legacy receipt should be cleared on save. Edit mode only. */
  onClearLegacy?: () => void;
  legacyRemoved?: boolean;
  /** In CREATE mode, files are buffered here until the parent uploads after insert. */
  pendingFiles?: File[];
  onPendingFilesChange?: (files: File[]) => void;
  pasteActive?: boolean;
}

export const ExpenseReceiptsManager = ({
  expenseId,
  legacyReceiptPath,
  onClearLegacy,
  legacyRemoved,
  pendingFiles,
  onPendingFilesChange,
  pasteActive,
}: Props) => {
  const isEdit = !!expenseId;
  const [buffer, setBuffer] = useState<File[]>([]);
  const { data: attachments, isLoading } = useExpenseAttachments(isEdit ? expenseId : null);
  const uploadMut = useUploadExpenseAttachments();
  const deleteMut = useDeleteExpenseAttachment();

  const openSigned = async (path: string) => {
    const { data } = await supabase.storage.from("expense-receipts").createSignedUrl(path, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
    else toast.error("Could not open file");
  };

  const onAddFiles = async (files: File[]) => {
    if (!isEdit) {
      onPendingFilesChange?.(files);
      return;
    }
    // Edit mode — upload diff immediately
    const existing = new Set(buffer.map((f) => `${f.name}|${f.size}`));
    const newOnes = files.filter((f) => !existing.has(`${f.name}|${f.size}`));
    if (newOnes.length === 0) {
      setBuffer(files);
      return;
    }
    setBuffer(files);
    try {
      const res = await uploadMut.mutateAsync({ expense_id: expenseId!, files: newOnes });
      if (res.uploaded > 0) toast.success(`${res.uploaded} receipt${res.uploaded === 1 ? "" : "s"} uploaded`);
      if (res.failed.length > 0) {
        res.failed.forEach((f) => toast.error(`${f.name}: ${f.error}`));
      }
    } catch (e: any) {
      toast.error(e.message || "Upload failed");
    } finally {
      setBuffer([]);
    }
  };

  return (
    <div className="space-y-2 min-w-0">


      {/* Edit-mode: existing attachments + legacy */}
      {isEdit && (
        <div className="space-y-1.5">
          {legacyReceiptPath && !legacyRemoved && (
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
              <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-sm flex-1 truncate">Receipt (legacy)</span>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => openSigned(legacyReceiptPath)}>
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={() => onClearLegacy?.()}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            attachments?.map((att: ExpenseAttachment) => (
              <div key={att.id} className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 min-w-0">
                <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm truncate">{att.file_name}</p>
                  <p className="text-[10px] text-muted-foreground">{fmtSize(att.file_size)}</p>
                </div>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => openSigned(att.file_path)}>
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  disabled={deleteMut.isPending}
                  onClick={async () => {
                    try {
                      await deleteMut.mutateAsync(att);
                      toast.success("Receipt removed");
                    } catch (e: any) {
                      toast.error(e.message || "Delete failed");
                    }
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Drop zone — multi */}
      <ReceiptDropZone
        multiple
        files={isEdit ? buffer : pendingFiles ?? []}
        onFilesChange={onAddFiles}
        pasteActive={pasteActive}
      />
      {uploadMut.isPending && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Uploading…
        </div>
      )}
    </div>
  );
};

export default ExpenseReceiptsManager;
